import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';
import Banner from '../components/Banner';
import '../css/global.css';
import '../css/DrydocksPage.css';

/* ── Image gallery per dock ── */
const DOCK_IMAGES = {
  1: [
    '/wp-content/uploads/2016/02/dock1-1.jpg',
    '/wp-content/uploads/2016/02/dock1-2.jpg',
    '/wp-content/uploads/2016/02/dock1-3.jpg',
    '/wp-content/uploads/2016/02/dock1-5.jpg',
    '/wp-content/uploads/2016/02/dock1-4.jpg',
    '/wp-content/uploads/2016/02/dock2-5.jpg',
    '/wp-content/uploads/2016/02/dock2-1.jpg',
    '/wp-content/uploads/2016/02/dock2-3.jpg',
  ],
  2: [
    '/wp-content/uploads/2016/02/dock2-4.jpg',
    '/wp-content/uploads/2016/02/d1.jpg',
    '/wp-content/uploads/2016/02/IMGA0028.png',
    '/wp-content/uploads/2016/02/P1010061.png',
    '/wp-content/uploads/2016/02/P1010182.png',
    '/wp-content/uploads/2016/02/P1180098.png',
    '/wp-content/uploads/2016/02/P2160074.png',
    '/wp-content/uploads/2016/02/P2280101.png',
    '/wp-content/uploads/2016/02/P3150077.png',
  ],
  3: [
    '/wp-content/uploads/2016/02/dock3-1.jpg',
    '/wp-content/uploads/2016/02/dock3-2.jpg',
    '/wp-content/uploads/2016/02/dock3-3.jpg',
    '/wp-content/uploads/2016/02/dock3-4.jpg',
    '/wp-content/uploads/2016/02/dock3-5.jpg',
  ],
  4: [
    '/wp-content/uploads/2016/02/dock4-4.jpg',
    '/wp-content/uploads/2016/02/dock5-1.jpg',
    '/wp-content/uploads/2016/02/dock5-2.jpg',
    '/wp-content/uploads/2016/02/dock5-3.jpg',
    '/wp-content/uploads/2016/02/dock5-5.jpg',
  ],
};

/* ── Reusable image gallery component ── */
function DockGallery({ dockKey }) {
  const images = DOCK_IMAGES[dockKey] || [];
  const [current, setCurrent] = useState(0);

  const prev = useCallback(() =>
    setCurrent(c => (c - 1 + images.length) % images.length), [images.length]);

  const next = useCallback(() =>
    setCurrent(c => (c + 1) % images.length), [images.length]);

  /* Auto-advance every 3 s */
  useEffect(() => {
    const t = setInterval(next, 3000);
    return () => clearInterval(t);
  }, [next]);

  if (!images.length) return null;

  return (
    <div className="dock-gallery">
      <img src={images[current]} alt={`Dock ${dockKey}`} loading="lazy" />
      {images.length > 1 && (
        <>
          <button className="gallery-nav prev" onClick={prev} aria-label="Previous">&#8249;</button>
          <button className="gallery-nav next" onClick={next} aria-label="Next">&#8250;</button>
          <div className="gallery-controls">
            {images.map((_, i) => (
              <button
                key={i}
                className={`gallery-dot ${i === current ? 'active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Specs table shared component ── */
function SpecsTable({ rows }) {
  return (
    <div className="table-wrapper">
      <table style={{ width: '100%' }}>
        <thead>
          <tr style={{ backgroundColor: '#387AB5' }}>
            {['Dock No', 'Length (m)', 'Breadth (m)', 'Depth (m)', 'Capacity (Dwt)', 'Cranage (t)'].map(h => (
              <td key={h} style={{ textAlign: 'center', color: '#fff', fontWeight: 'bold', padding: '10px' }}>{h}</td>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#B6C9D9' : '#809CB4' }}>
              {row.map((cell, j) => (
                <td key={j} style={{ textAlign: 'center', color: '#000', padding: '10px' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ── Dock tab data ── */
const TABS = [
  {
    id: 'DockNo-1',
    label: 'Colombo Dockyard Dock No:1',
    dockKey: 1,
    specsRows: [
      ['Drydock No. 1A', '148', '26', '9.7', '30,000', '160'],
      ['Drydock No. 1B (Shipbuilding)', '62', '26', '9.7', '—', '160'],
    ],
    description: (
      <p>
        Drydock No. 01 (Separated as Dock 1A &amp; 1B) is a graving dock utilised for serving General Cargo,
        Feeder Container carriers, Offshore Support Vessels, Passenger vessels and dredgers. Dock No. 1A is
        used for Ship Repairs, while 1B is utilised for unit assembly.
      </p>
    ),
    extraLink: null,
  },
  {
    id: 'DockNo-2',
    label: 'Colombo Dockyard Dock No:2',
    dockKey: 2,
    specsRows: [
      ['Drydock No. 2 (Shipbuilding)', '107', '21.5', '6.7', '9,000', '160'],
    ],
    description: (
      <p>
        Drydock No. 02 located in tandem to Drydock No. 01 is exclusively utilised for shipbuilding projects.
        This building dock is sheltered with moving sheds, providing ideal building conditions for yard teams.
      </p>
    ),
    extraLink: null,
  },
  {
    id: 'DockNo-3',
    label: 'Colombo Dockyard Dock No:3',
    dockKey: 3,
    specsRows: [
      ['Drydock No. 3', '122', '16', '5.5', '8,000', '20'],
    ],
    description: (
      <p style={{ textAlign: 'justify' }}>
        Drydock No. 03: Termed as Submarine Dock (Initially designed to take in submarines during the World War 2)
        is predominantly utilised to accommodate Work boats, naval vessels, Tugs, Fishing Trawlers, Passenger
        vessels and smaller Offshore Support vessels.
      </p>
    ),
    extraLink: null,
  },
  {
    id: 'DockNo-4',
    label: 'Colombo Dockyard Dock No:4',
    dockKey: 4,
    specsRows: [
      ['Drydock No. 4', '263', '44', '8.9', '125,000', '50'],
    ],
    description: (
      <>
        <p style={{ textAlign: 'justify' }}>
          Drydock No. 04: 125,000 DWT capacity drydock built during the late 1980&#8217;s is the biggest
          capable of handling AFRA MAX SIZE vessels comfortably. This drydock is geared with Dock Arms (450 Kg),
          Vehicle access tunnel and serviced with 3 cranes Maximum 50 Tons.
        </p>
        <p>
          <strong>
            <a
              className="dwnload-icon-blue"
              href="/wp-content/uploads/2016/06/AFRAMAX VESSELS IN COLOMBO DOCKYARD.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Aframax vessels in colombo dockyard
            </a>
          </strong>
        </p>
      </>
    ),
    extraLink: null,
  },
];

const REPAIR_SERVICES = [
  'High pressure water jetting and cleaning',
  'Grit / shot blasting',
  'Airless spray painting',
  'Overhauling / installation of undersea fitting',
  'Withdrawal of tail-end shafts',
  'Renewal of stern bushes',
  'Overhauling and renewal of stern seals',
  'Unshipping of rudders',
  'Checking and correcting alignment of rudders',
  'Replacement of anodes',
  'Installation and repair of cathodic protection systems',
  'Non-destructive methods for testing radiography',
  'Lignum vitae re-bushing',
  'Ultrasonic x-rays and magnetic particle surveys',
  'Load line inspections',
  'Safety equipment surveys',
  'Certifications',
];

/* ── Main Page Component ── */
const DrydocksPage = () => {
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="drydocks-page">
      <Header />

      <Banner backgroundImage="/wp-content/uploads/2016/02/drydocks.jpg" />

      <section className="contentwrapper clearfix">
        <div className="content clearfix">
          {/* Breadcrumbs */}
          <div className="breadcrumbs">
            <Link to="/" className="home">Home</Link> &gt; <span>Drydocks</span>
          </div>

          <h1>Drydocks</h1>

          <div id="drydocks-wrapper">
            {/* Intro paragraph */}
            <p style={{ textAlign: 'justify', marginBottom: '20px' }}>
              Colombo Dockyard is a state of the art engineering entity that operates four graving drydocks with a
              maximum capacity of 125,000 Dwt and extensive repair berth facilities along with fully fledged workshop
              facilities
            </p>

            {/* Summary specs table */}
              <SpecsTable
                rows={[
                  ['Drydock No. 1A',             '148', '26', '9.7', '30,000',  '160'],
                  ['Drydock No. 1B (Shipbuilding)', '62', '26', '9.7', '—',     '160'],
                  ['Drydock No. 2 (Shipbuilding)', '107', '24', '6.7', '9,000', '160'],
                  ['Drydock No. 3',              '122', '16', '5.5', '8,000',   '20'],
                  ['Drydock No. 4',              '263', '44', '8.9', '125,000', '70'],
                ]}
              />

            {/* Tabs */}
            <div className="drydocks-tabs">
              <ul className="drydocks-tab-list" role="tablist">
                {TABS.map((tab, idx) => (
                  <li key={tab.id} role="presentation">
                    <button
                      id={`tab-btn-${tab.id}`}
                      role="tab"
                      aria-selected={activeTab === idx}
                      aria-controls={`tabpanel-${tab.id}`}
                      className={activeTab === idx ? 'active' : ''}
                      onClick={() => setActiveTab(idx)}
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>

              {TABS.map((tab, idx) => (
                <div
                  key={tab.id}
                  id={`tabpanel-${tab.id}`}
                  role="tabpanel"
                  aria-labelledby={`tab-btn-${tab.id}`}
                  className={`drydocks-tab-panel ${activeTab === idx ? 'active' : ''}`}
                >
                  {/* Per-dock specs */}
                  <SpecsTable rows={tab.specsRows} />

                  {/* Gallery + description */}
                  <div className="dock-panel-row">
                    <div className="dock-panel-gallery-col">
                      <DockGallery dockKey={tab.dockKey} />
                    </div>
                    <div className="dock-panel-text-col">
                      {tab.description}
                    </div>
                  </div>

                  {/* Map link */}
                  <div className="dock-map-link">
                    <Link to="/shipyard-layout">View interactive dockyard layout map</Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Parallax repairs section */}
      <div
        className="drydocks-repairs-section"
        style={{ backgroundImage: "url('/wp-content/uploads/2016/02/IMG_2961.jpg')" }}
      >
        <div className="drydocks-repairs-inner" data-aos="fade-up">
          <h3 data-aos="fade-right"><strong>Our extensive list of drydock repairs undertaken include:</strong></h3>
          <ul>
            {REPAIR_SERVICES.map((item, i) => (
              <li key={i} data-aos="fade-right" data-aos-delay={i * 50}><strong>{item}</strong></li>
            ))}
          </ul>
        </div>
      </div>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default DrydocksPage;
